import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Key, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function SetPasswordDialog({ open, onOpenChange, userId, userName, userEmail }) {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setPassword(pw);
  };

  const handleSave = async () => {
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post(`/admin/users/${userId}/set-password`, { password });
      toast.success(`Password updated for ${userName}`);
      onOpenChange(false);
      setPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set password');
    } finally { setLoading(false); }
  };

  const copyCredentials = () => {
    navigator.clipboard.writeText(`Email: ${userEmail}\nPassword: ${password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Credentials copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setPassword(''); }}>
      <DialogContent className="max-w-md" data-testid="set-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-[#C9A84C]" /> Set Password</DialogTitle>
          <DialogDescription>Set login credentials for {userName} ({userEmail})</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-sm"><span className="font-semibold text-slate-600">User:</span> {userName}</p>
            <p className="text-sm"><span className="font-semibold text-slate-600">Email:</span> {userEmail}</p>
          </div>
          <div>
            <Label>New Password</Label>
            <div className="relative mt-1">
              <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter new password" data-testid="set-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex gap-2 mt-2">
              <Button type="button" variant="outline" size="sm" onClick={generatePassword} data-testid="generate-password-btn">
                Generate Strong Password
              </Button>
              {password && (
                <Button type="button" variant="outline" size="sm" onClick={copyCredentials} data-testid="copy-credentials-btn">
                  {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                  {copied ? 'Copied!' : 'Copy Credentials'}
                </Button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setPassword(''); }}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading} className="bg-[#1B2A4A]" data-testid="confirm-set-password-btn">
            {loading ? 'Saving...' : 'Set Password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
